import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { History, Mic, Plus, X } from "lucide-react";
import { useCopilot } from "@/context/CopilotContext";
import CopilotPanel from "./CopilotPanel";
import ConversationList from "./ConversationList";

// Persistent Copilot launcher (COP-UX-136/140): available across authenticated dealer
// surfaces without abandoning the current module. Desktop: expandable contextual
// panel with a path into the full workspace; mobile: full-screen conversation.
export default function CopilotLauncher() {
  const { panelOpen, openPanel, closePanel, openWithContext, sessionId, setSessionId } = useCopilot();
  const [historyOpen, setHistoryOpen] = useState(false);
  const location = useLocation();
  const onWorkspace = location.pathname === "/dealer/copilot";

  useEffect(() => {
    const handler = (e) => openWithContext(e.detail || null);
    window.addEventListener("copilot:open", handler);
    return () => window.removeEventListener("copilot:open", handler);
  }, [openWithContext]);

  if (onWorkspace) return null;

  return (
    <>
      {!panelOpen && (
        <button
          onClick={openPanel}
          className="fixed bottom-5 right-5 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-terracotta text-white shadow-lg transition-all duration-200 hover:-translate-y-[1px] hover:bg-terracotta-hover"
          data-testid="copilot-launcher-button"
          aria-label="Open ZELI"
        >
          <Mic className="h-5 w-5" />
        </button>
      )}
      {panelOpen && (
        <>
          <div className="fixed inset-0 z-40 bg-charcoal/30 md:hidden" onClick={closePanel} data-testid="copilot-panel-backdrop" />
          <div
            className="fixed inset-0 z-50 flex flex-col bg-background md:inset-y-0 md:left-auto md:right-0 md:w-[420px] md:border-l md:border-border md:shadow-xl"
            data-testid="copilot-launcher-panel"
          >
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <p className="font-display text-lg" data-testid="copilot-brand-lockup">
                <span className="text-terracotta">ZELI</span>
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setHistoryOpen((v) => !v)}
                  className="flex h-8 w-8 items-center justify-center rounded-full text-warmgrey transition-colors hover:bg-sand hover:text-charcoal"
                  data-testid="copilot-history-toggle"
                  aria-label="Recent conversations"
                  aria-expanded={historyOpen}
                >
                  <History className="h-4 w-4" />
                </button>
                <button
                  onClick={() => { setSessionId(null); setHistoryOpen(false); }}
                  className="flex h-8 w-8 items-center justify-center rounded-full text-warmgrey transition-colors hover:bg-sand hover:text-charcoal"
                  data-testid="copilot-new-conversation"
                  aria-label="New conversation"
                >
                  <Plus className="h-4 w-4" />
                </button>
                <button
                  onClick={closePanel}
                  className="flex h-8 w-8 items-center justify-center rounded-full text-warmgrey transition-colors hover:bg-sand hover:text-charcoal"
                  data-testid="copilot-panel-close"
                  aria-label="Close ZELI"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
            {historyOpen && (
              <div className="max-h-64 overflow-y-auto border-b border-border bg-white p-2" data-testid="copilot-panel-history">
                <ConversationList key={sessionId || "fresh"} currentId={sessionId} onSelect={(id) => { setSessionId(id); setHistoryOpen(false); }} />
              </div>
            )}
            <div className="min-h-0 flex-1">
              <CopilotPanel variant="panel" />
            </div>
          </div>
        </>
      )}
    </>
  );
}
